import React, { useState, useEffect } from "react";
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { useSearchParams, useLocation } from "react-router-dom";
import TeamService from "../services/teamService";

const SignUp = ({
  inviteToken: inviteTokenProp,
  onSignupSuccess,
  onNavigateToLogin,
  onNavigateToDashboard,
  isAuthenticated,
  currentUser,
}) => {
  const [activeTab, setActiveTab] = useState("register");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Check for invite token in URL OR prop
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const inviteToken = inviteTokenProp || searchParams.get("invite");
  const resetSuccess = searchParams.get("resetSuccess");

  // Form states
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: "",
  });

  const [registerForm, setRegisterForm] = useState({
    name: "",
    email: "",
    role: "",
    password: "",
    password_confirmation: "",
  });

  // Check for state from reset password page and URL params
  useEffect(() => {
    // Check URL params for reset success
    if (resetSuccess === "true") {
      setSuccess("Password reset successful! Please log in with your new password.");
      setActiveTab("login");
    }

    // Check location state from ResetPasswordPage
    if (location.state) {
      if (location.state.email) {
        setLoginForm(prev => ({
          ...prev,
          email: location.state.email
        }));
      }
      
      if (location.state.successMessage) {
        setSuccess(location.state.successMessage);
      }
      
      if (location.state.activeTab === "login") {
        setActiveTab("login");
      }
    }

    // Check for email in URL params (backup method)
    const emailFromParams = searchParams.get("email");
    if (emailFromParams) {
      setLoginForm(prev => ({
        ...prev,
        email: emailFromParams
      }));
      setActiveTab("login");
    }

    // Check for success message in URL params (backup method)
    const messageFromParams = searchParams.get("message");
    if (messageFromParams) {
      setSuccess(messageFromParams);
      setActiveTab("login");
    }
  }, [location.state, searchParams, resetSuccess]);

  // Fetch invite details if inviteToken exists
  useEffect(() => {
    const fetchInvite = async () => {
      if (!inviteToken) {
        setLoadingInvite(false);
        return;
      }
      try {
        const result = await TeamService.getInvite(inviteToken);
        if (result.success && result.data) {
          setRegisterForm((prev) => ({
            ...prev,
            email: result.data.email,
            role: result.data.role,
          }));
        } else {
          setError(result.message || "Invalid or expired invite link.");
        }
      } catch (err) {
        setError(err.message || "Failed to fetch invite details.");
      } finally {
        setLoadingInvite(false);
      }
    };
    fetchInvite();
  }, [inviteToken]);

  // Handle login form change
  const handleLoginChange = (e) => {
    setLoginForm({
      ...loginForm,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  // Handle register form change
  const handleRegisterChange = (e) => {
    setRegisterForm({
      ...registerForm,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  // Handle login submit
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const response = await TeamService.login(
        loginForm.email,
        loginForm.password
      );

      if (response.data && response.data.token) {
        localStorage.setItem("userId", response.data.user.id);
        localStorage.setItem("userName", response.data.user.name);
        localStorage.setItem("Role", response.data.user.role);
        localStorage.setItem("authToken", response.data.token);
        setSuccess("Login successful! Redirecting...");
        if (onNavigateToDashboard) onNavigateToDashboard();
      }
    } catch (err) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle register submit
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (registerForm.password !== registerForm.password_confirmation) {
      setError("Passwords do not match");
      return;
    }
    if (registerForm.password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);

    try {
      const payload = { ...registerForm, inviteToken };
      const response = await TeamService.register(payload);

      if (response.success) {
        setSuccess("Account created successfully! Redirecting...");
        if (onSignupSuccess) onSignupSuccess();
      }
    } catch (err) {
      setError(err.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Clear success message when switching tabs
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setError("");
    setSuccess("");
  };

  if (loadingInvite) {
    return <div className="text-center mt-20">Loading invite details...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="max-w-xl w-full space-y-8">
        <div className="flex justify-center mb-6">
          <img
            src="/images/FibreLogo.jpg"
            alt="Company Logo"
            className="h-16 w-auto"
          />
        </div>
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900">Team Portal</h2>
          <p className="mt-2 text-sm text-gray-600">
            {activeTab === "login"
              ? "Sign in to your account"
              : "Create a new team account"}
          </p>
        </div>

        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => handleTabSwitch("login")}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                activeTab === "login"
                  ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => handleTabSwitch("register")}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                activeTab === "register"
                  ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Sign Up
            </button>
          </div>

          <div className="p-6">
            {/* Error/Success Messages */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700">
                <AlertCircle className="h-5 w-5 mr-2" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center text-green-700">
                <CheckCircle2 className="h-5 w-5 mr-2" />
                <span className="text-sm">{success}</span>
              </div>
            )}

            {/* Login Form */}
            {activeTab === "login" && (
              <form onSubmit={handleLoginSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="login-email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="login-email"
                      name="email"
                      type="email"
                      required
                      value={loginForm.email}
                      onChange={handleLoginChange}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="login-password"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="login-password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      required
                      value={loginForm.password}
                      onChange={handleLoginChange}
                      className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <div className="text-sm">
                    <button
                      type="button"
                      onClick={() => window.location.href = "/forgot-password"}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                      Signing in...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </button>

                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => handleTabSwitch("register")}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Don't have an account? Sign Up
                  </button>
                </div>
              </form>
            )}

            {/* Register Form */}
           
            {activeTab === "register" && (
              <form onSubmit={handleRegisterSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="register-name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Full Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="register-name"
                      name="name"
                      type="text"
                      required
                      value={registerForm.name}
                      onChange={handleRegisterChange}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="John Doe"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="register-email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="register-email"
                      name="email"
                      type="email"
                      required
                      value={registerForm.email}
                      onChange={handleRegisterChange}
                      readOnly={!!inviteToken}
                      className={`block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md ${
                        inviteToken ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="register-role"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Role
                  </label>
                  <select
                    id="register-role"
                    name="role"
                    required
                    value={registerForm.role}
                    onChange={handleRegisterChange}
                    disabled={!!inviteToken}
                    className={`block w-full px-3 py-2 border border-gray-300 rounded-md ${
                      inviteToken ? "bg-gray-100 cursor-not-allowed" : ""
                    }`}
                  >
                    {inviteToken ? (
                      <option>{registerForm.role}</option>
                    ) : (
                      <>
                        <option value="">Select a role</option>
                        <option value="Admin">Admin</option>
                        <option value="Manager">Manager</option>
                        <option value="Team Member">Team Member</option>
                        <option value="Support">Support</option>
                      </>
                    )}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="register-password"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="register-password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      required
                      value={registerForm.password}
                      onChange={handleRegisterChange}
                      className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Minimum 8 characters"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="register-confirm-password"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Confirm Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="register-confirm-password"
                      name="password_confirmation"
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      value={registerForm.password_confirmation}
                      onChange={handleRegisterChange}
                      className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                      Creating account...
                    </>
                  ) : (
                    "Create Account"
                  )}
                </button>

                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => handleTabSwitch("login")}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Already have an account? Sign In
                  </button>
                </div>
              </form>
            )}
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;